import { memo, useCallback, useContext } from 'react'

import { route } from '@audius/common/utils'
import { useLocation } from 'react-router'

import { RouterContext } from 'components/animated-switch/RouterContextProvider'
import ExploreButton from 'components/bottom-bar/buttons/ExploreButton'
import FeedButton from 'components/bottom-bar/buttons/FeedButton'
import HomeButton from 'components/bottom-bar/buttons/HomeButton'
import NotificationsButton from 'components/bottom-bar/buttons/NotificationsButton'
import TrendingButton from 'components/bottom-bar/buttons/TrendingButton'

import styles from './BottomBar.module.css'

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }
}

const {
  FEED_PAGE,
  TRENDING_PAGE,
  EXPLORE_PAGE,
  HOMEPAGE_PAGE,
  NOTIFICATION_PAGE
} = route

type Props = {
  currentPage: string
  onClickHome: () => void
  onClickFeed: () => void
  onClickTrending: () => void
  onClickExplore: () => void
  onClickNotifications: () => void
  isDarkMode: boolean
  isMatrixMode: boolean
}

const BottomBar = ({
  currentPage,
  onClickHome,
  onClickFeed,
  onClickTrending,
  onClickExplore,
  onClickNotifications,
  isDarkMode,
  isMatrixMode
}: Props) => {
  const { setStackReset } = useContext(RouterContext)
  const location = useLocation()
  const { pathname } = location

  const onClick = useCallback(
    (callback: () => void, page: string | null) => () => {
      if (page === pathname) {
        window.scrollTo(0, 0)
      } else {
        setStackReset(true)
        callback()
      }
    },
    [setStackReset, pathname]
  )

  return window.ReactNativeWebView?.postMessage ? null : (
    <div className={styles.bottomBar}>
      <HomeButton
        isActive={currentPage === HOMEPAGE_PAGE}
        darkMode={isDarkMode}
        onClick={onClick(onClickHome, HOMEPAGE_PAGE)}
        href={HOMEPAGE_PAGE}
        isMatrixMode={isMatrixMode}
        aria-label='Home Page'
      />
      <TrendingButton
        isActive={currentPage === TRENDING_PAGE}
        darkMode={isDarkMode}
        onClick={onClick(onClickTrending, TRENDING_PAGE)}
        href={TRENDING_PAGE}
        isMatrixMode={isMatrixMode}
        aria-label='Trending Page'
      />
      <FeedButton
        isActive={currentPage === FEED_PAGE}
        darkMode={isDarkMode}
        onClick={onClick(onClickFeed, FEED_PAGE)}
        href={FEED_PAGE}
        isMatrixMode={isMatrixMode}
        aria-label='Feed Page'
      />
      <ExploreButton
        isActive={currentPage === EXPLORE_PAGE}
        darkMode={isDarkMode}
        onClick={onClick(onClickExplore, EXPLORE_PAGE)}
        href={EXPLORE_PAGE}
        isMatrixMode={isMatrixMode}
        aria-label='Explore Page'
      />
      <NotificationsButton
        isActive={currentPage === NOTIFICATION_PAGE}
        darkMode={isDarkMode}
        onClick={onClick(onClickNotifications, NOTIFICATION_PAGE)}
        href={NOTIFICATION_PAGE}
        isMatrixMode={isMatrixMode}
        aria-label='Notifications Page'
      />
    </div>
  )
}

export default memo(BottomBar)
